/**
 * useModules — centrale moduledefinities voor de admin-instellingen.
 *
 * Eén bron van waarheid voor de modules-index (`/admin/settings/modules`) én de
 * secondaryNav in `pages/admin/settings.vue`. Elke module is een boolean-veld op
 * de `company`-record. Voorheen waren deze definities impliciet verspreid over
 * `general.vue` (de toggles) en `settings.vue` (de conditionele nav-tabs); deze
 * lijst bundelt label, beschrijving, slug en config-status op één plek.
 */

/**
 * @typedef {Object} ModuleDef
 * @property {string|null} key - boolean-veld op de company-record (bv. `enable_invoicing`), of null bij een altijd-actieve module
 * @property {string} slug - URL-segment onder `/admin/settings/modules/<slug>`
 * @property {string} name - korte naam in de secondaryNav
 * @property {string} [label] - label van de toggle op de index; ontbreekt bij een altijd-actieve module
 * @property {string} [description] - beschrijving onder de toggle; ontbreekt bij een altijd-actieve module
 * @property {string} [activeNote] - extra zin die alleen toont als de module aan staat
 * @property {boolean} hasConfig - of de module een eigen config-subpagina heeft
 * @property {boolean} [alwaysActive] - staat altijd in de nav en krijgt geen toggle op de index
 */

/** @type {ModuleDef[]} */
export const MODULES = [
  {
    key: null,
    slug: 'urenregistratie',
    name: 'Urenregistratie',
    alwaysActive: true,
    hasConfig: true,
  },
  {
    key: 'enable_employee_payslips',
    slug: 'loonstroken',
    name: 'Loonstroken',
    label: 'Activeer loonstroken voor werknemers',
    description: 'Dit geeft de mogelijkheid tot het uploaden van loonstroken bij de medewerkers die zij kunnen inzien via hun app.',
    hasConfig: false,
  },
  {
    key: 'enable_kilometer_registration',
    slug: 'kilometer-registratie',
    name: 'Kilometer registratie',
    label: 'Activeer kilometer registratie',
    description: 'Activeer kilometer registratie voor werknemers. Zowel handmatige KM invoer als automatische berekeningen.',
    activeNote: 'Standaard alleen NL adressen. Andere landen op aanvraag (kosteloos)',
    hasConfig: true,
  },
  {
    key: 'enable_invoicing',
    slug: 'facturen',
    name: 'Facturen',
    label: 'Activeer facturatiemodule',
    description: 'Voeg tarieven toe aan werknemers, materialen en kilometers. Download projecten en maak direct PDF facturen op.',
    hasConfig: true,
  },
  {
    key: 'enable_project_signing',
    slug: 'aftekenen',
    name: 'Digitaal aftekenen',
    label: 'Digitaal aftekenen van werkbonnen',
    description: 'Laat de opdrachtgever de werkbon ter plekke ondertekenen op het toestel van de werknemer.',
    hasConfig: false,
  },
];

/**
 * Geactiveerde modules voor een company — de bron voor de secondaryNav.
 * @param {Record<string, any>} company - de company-record uit de store
 * @returns {ModuleDef[]} modules die altijd actief zijn plus die waarvan het company-veld truthy is, in MODULES-volgorde
 */
export function activeModules(company) {
  return MODULES.filter((mod) => mod.alwaysActive || company?.[mod.key]);
}

/**
 * Zoek een moduledefinitie op slug (voor de subpagina's).
 * @param {string} slug - URL-segment
 * @returns {ModuleDef | undefined}
 */
export function moduleBySlug(slug) {
  return MODULES.find((mod) => mod.slug === slug);
}
